import { ScrollArea } from "@base-ui/react/scroll-area"
import React from "react"
import { Tab, Tabs } from "react-bootstrap"

import classNames from "classnames"

import { VariantKeyDisplay } from "entities/products/ui/variant-key-display/VariantKeyDisplay"

import { ProductConfiguratorSummaryCardHeader } from "./ProductConfiguratorSummaryCardHeader"
import { useProductConfiguratorSummaryCardViewModel } from "./hooks/useProductConfiguratorSummaryCardViewModel"

export type ProductConfiguratorSummaryCardViewModel = ReturnType<
  typeof useProductConfiguratorSummaryCardViewModel
>

function SummaryCardScrollArea({
  children,
  isUpdating
}: {
  children: React.ReactNode
  isUpdating?: boolean
}) {
  return (
    <ScrollArea.Root
      className={classNames(
        "scroll-area scroll-area--vertical product-configurator-summary-card__scroll-area",
        {
          "product-configurator-summary-card__scroll-area--updating": isUpdating
        }
      )}
      // The content is the previous (stale) data while refetching, so it's hidden
      // from assistive tech and interaction instead of being read/used as current.
      inert={isUpdating}
    >
      <ScrollArea.Viewport className="scroll-area__viewport">
        <ScrollArea.Content className="scroll-area__content">
          {children}
        </ScrollArea.Content>
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="vertical"
        className="scroll-area__scrollbar"
      >
        <ScrollArea.Thumb className="scroll-area__thumb" />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  )
}

export const ProductConfiguratorSummaryCardView = React.memo(
  ({
    className,
    image,
    onZoomImage,
    shortTitle,
    kind,
    isLoading,
    isUpdating,
    showVariantKeysOnly,
    variantKeyDisplayProps,
    tabs,
    activeTab,
    onTabChange
  }: ProductConfiguratorSummaryCardViewModel) => (
    <section
      className={className}
      data-testid="product-configurator-summary"
      aria-busy={isUpdating}
    >
      <ProductConfiguratorSummaryCardHeader
        image={image}
        onZoomImage={onZoomImage}
        shortTitle={shortTitle}
        title={kind}
        isLoading={isLoading}
        isUpdating={isUpdating}
      />

      <div className="product-configurator-summary-card__body">
        {showVariantKeysOnly ? (
          <SummaryCardScrollArea isUpdating={isUpdating}>
            <VariantKeyDisplay {...variantKeyDisplayProps} />
          </SummaryCardScrollArea>
        ) : null}

        {!showVariantKeysOnly && tabs.length ? (
          <Tabs
            activeKey={activeTab ?? undefined}
            onSelect={onTabChange}
            className="product-configurator-summary-card__tabs"
          >
            {tabs.map(tab => (
              <Tab key={tab.key} eventKey={tab.key} title={tab.title}>
                <SummaryCardScrollArea isUpdating={isUpdating}>
                  {tab.content}
                </SummaryCardScrollArea>
              </Tab>
            ))}
          </Tabs>
        ) : null}
      </div>
    </section>
  )
)
